import classNames from 'classnames';
import type { ComponentProps, ReactNode } from 'react';
import type { To } from 'react-router';
import styled, { keyframes } from 'styled-components';

import { NavLink } from '@sorare/routing';

import { Horizontal, Vertical } from 'atoms/layout/flex';
import { Tooltip } from 'atoms/tooltip/Tooltip';
import { LabelM } from 'atoms/typography';
import { Badge } from 'atoms/ui/Badge';
import { LiveDot } from 'components/live/LiveDot';
import { useContextNavigation } from 'hooks/useContextNavigation';
import { breakpoints, laptopAndAbove } from 'style/mediaQuery';

const hideNavItemLabel = keyframes`
  from {
    /* this is the width of the sidebar non-collapsed */
    max-width: 240px;
  }
  to {
    max-width: calc(5 * var(--unit));
    overflow: hidden;
  }
`;

const Root = styled(Horizontal)`
  justify-content: center;
  width: 100%;
  cursor: pointer;
  color: var(--c-nd-600);

  &.horizontal {
    justify-content: flex-start;
    padding: var(--intermediate-unit);
    border-radius: var(--unit);

    &:not(.unactive) {
      &&.active {
        background-color: var(--c-nd-50);
      }
    }
    &:hover {
      background-color: rgba(var(--c-rgb-white), 0.04);
      color: var(--c-white);
    }
    &.sidebarCollapsed {
      animation: 300ms ${hideNavItemLabel} ease-in-out forwards;
    }
    &:not(.active).sidebarCollapsed:hover {
      transform: scale(1.2);
    }
    transition: 350ms transform cubic-bezier(0.02, 0.49, 0.51, 0.99);
  }
  &:not(.unactive) {
    &.active {
      color: var(--c-white);
    }
  }
`;
const Layout = styled(Vertical)`
  position: relative;
  align-items: center;
  gap: var(--half-unit);
  justify-content: flex-start;
  width: 100%;

  .horizontal & {
    flex-direction: row;
    gap: var(--intermediate-unit);
  }
  @media ${laptopAndAbove} {
    ${Root}:not(.active, .sidebarCollapsed):hover > & {
      transform: translateX(var(--half-unit));
    }
  }
  transition: 350ms transform cubic-bezier(0.02, 0.49, 0.51, 0.99);
`;
const RelativeWrapper = styled(Horizontal)`
  position: relative;
`;
const IconWrapper = styled(Horizontal).attrs({ center: true })`
  min-width: var(--double-unit);
  min-height: var(--double-unit);

  .active.horizontal & {
    transform: scale(1.2);
  }
`;
const FullWidthSBHorizontal = styled(Horizontal)`
  width: 100%;
  flex: 1;
  justify-content: center;

  .horizontal & {
    justify-content: space-between;
    white-space: nowrap;
  }
`;
const PlayLiveDotWrapper = styled.div`
  position: absolute;
  top: calc(-1 * var(--unit));
  right: calc(-1 * var(--double-unit));
`;
const StyledBadge = styled(Badge)`
  margin-right: 0;
  .horizontal & {
    margin-right: var(--unit);
  }
`;
const NavLabel = styled.span`
  font: var(--t-label-xs);
  white-space: nowrap;
  @media (min-width: ${breakpoints.se}px) {
    font: var(--t-label-s);
  }
  .horizontal & {
    font: var(--t-label-l);
  }
`;

export const Separator = styled.div`
  width: 100%;
  height: 0px;
  border-top: 1px solid var(--c-nd-150);
`;

type Props = {
  to?: To;
  icon?: ({ isActive }: { isActive: boolean }) => ReactNode;
  label: string;
  controlledActive?: boolean;
  showLiveDot?: boolean;
  badge?: { content: ReactNode; ariaLabel?: string };
  variant?: 'horizontal' | 'vertical';
  sidebarCollapsed?: boolean;
  onClick?: () => void;
  keepSearchParams?: boolean;
  matches?: ComponentProps<typeof NavLink>['matches'];
  enableContextModeNavigation?: boolean;
  end?: ComponentProps<typeof NavLink>['end'];
};

const NavLinkContent = ({
  sidebarCollapsed,
  variant,
  label,
  badge,
  icon,
  isActive,
  showLiveDot,
}: Pick<
  Props,
  'sidebarCollapsed' | 'showLiveDot' | 'variant' | 'label' | 'badge' | 'icon'
> & { isActive?: boolean }) => {
  const isDesktopLayout = variant === 'horizontal';
  return (
    <Layout>
      <Tooltip
        skip={!sidebarCollapsed || variant === 'vertical'}
        title={<LabelM>{label}</LabelM>}
        placement="right"
      >
        <IconWrapper>
          <StyledBadge
            invisible={!badge || (isDesktopLayout && !sidebarCollapsed)}
            badgeContent={
              badge && (!isDesktopLayout || sidebarCollapsed)
                ? badge?.content
                : undefined
            }
            aria-label={
              badge && (!isDesktopLayout || sidebarCollapsed)
                ? badge?.ariaLabel
                : undefined
            }
          >
            {icon?.({ isActive: isActive ?? false })}
          </StyledBadge>
        </IconWrapper>
      </Tooltip>
      <FullWidthSBHorizontal>
        <RelativeWrapper>
          <NavLabel className="text-center">{label}</NavLabel>
          {!!showLiveDot && (
            <PlayLiveDotWrapper>
              <LiveDot animate size="md" />
            </PlayLiveDotWrapper>
          )}
        </RelativeWrapper>
        {!!(badge && isDesktopLayout) && (
          <StyledBadge
            badgeContent={badge?.content}
            aria-label={badge?.ariaLabel}
          />
        )}
      </FullWidthSBHorizontal>
    </Layout>
  );
};

export const NavItem = ({
  to,
  icon,
  label,
  controlledActive,
  showLiveDot,
  badge,
  variant = 'horizontal',
  sidebarCollapsed,
  enableContextModeNavigation = false,
  ...rest
}: Props) => {
  const computedTo =
    useContextNavigation({
      to: enableContextModeNavigation ? to : undefined,
    }) || to;

  if (computedTo) {
    return (
      <Root
        as={NavLink}
        to={computedTo}
        state={{
          fromNavItem: true,
        }}
        className={classNames({
          horizontal: variant === 'horizontal',
          active: controlledActive,
          unactive: controlledActive === false,
          sidebarCollapsed,
        })}
        {...rest}
      >
        {({ isActive }) => (
          <NavLinkContent
            sidebarCollapsed={sidebarCollapsed}
            variant={variant}
            label={label}
            badge={badge}
            icon={icon}
            isActive={
              controlledActive === undefined ? isActive : controlledActive
            }
            showLiveDot={showLiveDot}
          />
        )}
      </Root>
    );
  }

  return (
    <Root
      className={classNames({
        horizontal: variant === 'horizontal',
        active: controlledActive,
        unactive: controlledActive === false,
        sidebarCollapsed,
      })}
      {...rest}
    >
      <NavLinkContent
        sidebarCollapsed={sidebarCollapsed}
        variant={variant}
        label={label}
        badge={badge}
        icon={icon}
        showLiveDot={showLiveDot}
      />
    </Root>
  );
};
